/* Portal API client — fetch-based, same-origin (Caddy proxies /api/* to the
   FastAPI container). Access token lives in memory; the refresh token is an
   httpOnly cookie set by the server, so requests use credentials: 'include'
   and a 401 triggers a single transparent refresh + retry. */

import type {
  ItemRow,
  LoginResponse,
  Portfolio,
  PortfolioDisplay,
  ShareResponse,
  SharedPortfolio,
  UserAuthInfo,
} from './types';

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}
export function getAccessToken(): string | null {
  return accessToken;
}

function rawFetch(path: string, opts: RequestInit, auth: boolean): Promise<Response> {
  const headers = new Headers(opts.headers ?? {});
  if (auth && accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
  return fetch(`${API_BASE}${path}`, { ...opts, headers, credentials: 'include' });
}

async function doRefresh(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/api/v1/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    });
    if (!res.ok) return false;
    const j = (await res.json()) as { access_token?: string };
    if (!j.access_token) return false;
    accessToken = j.access_token;
    return true;
  } catch {
    return false;
  }
}

/** Attempt a token refresh, de-duplicating concurrent callers. */
export async function refreshSession(): Promise<boolean> {
  if (!refreshing) {
    refreshing = doRefresh().finally(() => {
      refreshing = null;
    });
  }
  return refreshing;
}

async function request<T>(path: string, opts: RequestInit = {}, auth = true): Promise<T> {
  let res = await rawFetch(path, opts, auth);
  if (res.status === 401 && auth) {
    const ok = await refreshSession();
    if (ok) res = await rawFetch(path, opts, auth);
  }
  if (!res.ok) {
    let detail = res.statusText || 'Request failed';
    try {
      const j = await res.json();
      if (j?.detail) detail = String(j.detail);
    } catch {
      /* keep default */
    }
    throw new ApiError(detail, res.status);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

function jsonBody(data: unknown): RequestInit {
  return { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) };
}

// ── Auth ────────────────────────────────────────────────────────────────────

export async function login(username: string, password: string): Promise<LoginResponse> {
  const data = await request<LoginResponse>('/api/v1/auth/login', jsonBody({ username, password }), false);
  accessToken = data.access_token;
  return data;
}

export async function fetchMe(): Promise<UserAuthInfo> {
  return request<UserAuthInfo>('/api/v1/auth/me');
}

export async function logout(): Promise<void> {
  try {
    await request<unknown>('/api/v1/auth/logout', jsonBody({}), false);
  } finally {
    accessToken = null;
  }
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await request<unknown>(
    '/api/v1/auth/change-password',
    jsonBody({ current_password: currentPassword, new_password: newPassword }),
  );
}

export async function acceptInvite(inviteToken: string, password: string): Promise<LoginResponse> {
  const data = await request<LoginResponse>(
    '/api/v1/auth/accept-invite',
    jsonBody({ invite_token: inviteToken, password }),
    false,
  );
  accessToken = data.access_token;
  return data;
}

// ── Catalogue ─────────────────────────────────────────────────────────────────

export async function fetchItems(): Promise<ItemRow[]> {
  return request<ItemRow[]>('/api/itemtbl/?page=1&page_size=5000');
}

// ── Portfolios ──────────────────────────────────────────────────────────────

export async function listPortfolios(): Promise<Portfolio[]> {
  return request<Portfolio[]>('/api/v1/portfolios');
}

export async function createPortfolio(payload: {
  title?: string;
  client?: string;
  event?: string;
  note?: string;
  cover_code?: string;
}): Promise<Portfolio> {
  return request<Portfolio>('/api/v1/portfolios', jsonBody(payload));
}

export async function getPortfolio(id: string): Promise<Portfolio> {
  return request<Portfolio>(`/api/v1/portfolios/${encodeURIComponent(id)}`);
}

export async function updatePortfolio(
  id: string,
  patch: Partial<{
    title: string;
    client: string;
    event: string;
    note: string;
    cover_code: string;
    status: string;
    display: PortfolioDisplay;
  }>,
): Promise<Portfolio> {
  return request<Portfolio>(`/api/v1/portfolios/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
}

export async function deletePortfolio(id: string): Promise<void> {
  await request<unknown>(`/api/v1/portfolios/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function replacePortfolioItems(id: string, itemCodes: string[]): Promise<Portfolio> {
  return request<Portfolio>(`/api/v1/portfolios/${encodeURIComponent(id)}/items`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ item_codes: itemCodes }),
  });
}

export async function sharePortfolio(id: string): Promise<ShareResponse> {
  return request<ShareResponse>(`/api/v1/portfolios/${encodeURIComponent(id)}/share`, jsonBody({}));
}

export async function fetchShared(token: string): Promise<SharedPortfolio> {
  return request<SharedPortfolio>(`/api/v1/shared/${encodeURIComponent(token)}`, {}, false);
}

/** Public product-photo URL for an item code. */
export function productPhotoUrl(code: string): string {
  return `${API_BASE}/api/branding/product-photo/${encodeURIComponent(code)}`;
}

/** Didier (CompanyTbl id=1) contact for the lookbook/PDF footer. Public. */
export async function fetchPortalContact(): Promise<{ email: string; phone: string | null }> {
  return request<{ email: string; phone: string | null }>('/api/portal-contact', {}, false);
}
